/**
 * 图表元素组件 — 使用 VChart 渲染柱状图 / 折线图 / 饼图
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import VChart from '@visactor/vchart';
import type { ISpec } from '@visactor/vchart';
import type { ChartElement as ChartElementType } from '@/components/canvas/document/publicTypes';
import type { ElementComponentProps } from '@/components/canvas/elements/types';
import { appendBlurFilter, composeContainerTransform } from '../group/containerTransform';
import { blurRadius } from '@/components/canvas/elements/model';

type ChartElementComponentProps = ElementComponentProps<ChartElementType>;

export function ChartElement({
  element,
  dragOffset = { dx: 0, dy: 0 },
  onSelect,
  onMouseDown,
}: ChartElementComponentProps) {
  const { dx, dy } = dragOffset;
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<InstanceType<typeof VChart> | null>(null);

  /**
   * 在 Workspace 尚未来得及设置 data-workspace-dragging 的短暂时间窗口内（mousedown 发生后
   * 首次 mousemove 之前），用本地 state 保证 VChart 浮层立即被屏蔽。
   */
  const [localDragging, setLocalDragging] = useState(false);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      setLocalDragging(true);
      onMouseDown?.(e, element);
    },
    [onMouseDown, element]
  );

  useEffect(() => {
    if (!localDragging) return;
    const onUp = () => setLocalDragging(false);
    window.addEventListener('mouseup', onUp);
    return () => window.removeEventListener('mouseup', onUp);
  }, [localDragging]);

  const style: React.CSSProperties = {
    position: 'absolute',
    left: element.layout.x + dx,
    top: element.layout.y + dy,
    width: element.size.width,
    height: element.size.height,
    opacity: element.appearance.opacity,
    transform: composeContainerTransform(element),
    filter: appendBlurFilter(undefined, blurRadius(element)),
    // overflow 不设 hidden：VChart 的 tooltip 是 HTML <div>，绝对定位在图表容器内，
    // 悬停在边缘数据点时会出现在图表边界之外。overflow: hidden 会裁掉 tooltip，
    // 导致播放模式下 hover 无提示。VChart 的 <canvas> 元素自身已将图形裁剪在画布范围内，
    // 外层 div 不需要再做 overflow 裁剪。
  };

  const isDragging = localDragging || dx !== 0 || dy !== 0;

  useEffect(() => {
    const dom = containerRef.current;
    if (!dom) return;

    const spec = {
      ...(element.data.spec as Record<string, unknown>),
      width: element.size.width,
      height: element.size.height,
      autoFit: true,
      background: 'transparent',
      // 将 tooltip 的宿主元素设为图表自身的容器（dom），而不是 VChart 默认的 document.body。
      // VChart 的 tooltip-transformer.js 在浏览器环境下，若未指定 parentElement，会自动将
      // tooltip <div> 挂载到 document.body。全屏模式下 document.body 处于全屏层之外，
      // 导致 tooltip 不可见。将其固定在 dom（全屏容器的子元素）内可解决此问题。
      tooltip: {
        ...((element.data.spec as Record<string, unknown>).tooltip as object ?? {}),
        parentElement: dom,
      },
    } as unknown as ISpec;

    const chart = new VChart(spec, { dom });
    chartRef.current = chart;
    chart.renderAsync();

    return () => {
      chart.release();
      chartRef.current = null;
    };
  // Only re-mount when spec identity or chartType changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [element.data.spec, element.data.chartType]);

  useEffect(() => {
    chartRef.current?.resize(element.size.width, element.size.height);
  }, [element.size.width, element.size.height]);

  return (
    <div
      style={style}
      className="pointer-events-auto cursor-inherit"
      data-element-hit
      data-element-id={element.id}
      onClick={(e) => {
        e.stopPropagation();
        onSelect(e);
      }}
      onMouseDown={handleMouseDown}
    >
      {/* data-vchart-inner：配合 globals.css 中 [data-workspace-dragging] 规则屏蔽 VChart 指针事件 */}
      <div
        ref={containerRef}
        data-vchart-inner
        style={{
          width: '100%',
          height: '100%',
          pointerEvents: isDragging ? 'none' : undefined,
        }}
      />
    </div>
  );
}
